import React, {useEffect, useState} from 'react'
import {DropdownInput, DropdownOption, IconButton, Modal, Text} from "atmosfair-ui";
import {useAppDispatch, useAppSelector} from "../../../redux/reducer/store";
import {BundleType} from "../common";
import {useDeleteBundleMutation} from "../../../redux/reducer/api";
import {
    DEFAULT_TRAVEL_COMPONENTS,
    deleteTravelComponents,
    TravelComponentsState
} from "../../../redux/reducer/travel-components";
import {DEFAULT_JOURNEY, deleteJourney, JourneyState} from "../../../redux/reducer/journey";
import {UserProps} from "../../../api/model/user";
import {GENERAL_BACKEND_RESPONSE} from "../../../api/model/common";
import {i18n} from "i18next";
import {useTranslation} from "react-i18next";
import {isEqual} from "lodash";
import {
    convertDeleteBundlesButtonOptionsToDropdownFormat,
    DELETE_ACTIVITIES_DROPDOWN_SELECTION
} from "../../../utils/converter/dropdown";
import {toast, TypeOptions} from "react-toastify";
import ToastWithTextOnly from "../../../molecules/toasts/toast-with-text-only/ToastWithTextOnly";
import {AllActivityTypesArray} from "../../../types-enums/activity";

interface DeleteBundlesButtonProps {
    whatToDelete: BundleType,
    disabled?: boolean,
    activities?: AllActivityTypesArray
    onDelete?: () => void
}

const generateText = (
    whatToDelete: BundleType,
    bundleId: number | null,
    bundleName: string | number | null,
    bundleCreator: UserProps | null,
    i18n: i18n,
): Record<"option", DropdownOption> & Record<"modalHeadline" | "modalText", string> => {
    const {t} = i18n
    let userName = ""
    if (bundleCreator) {
        const {firstName, lastName} = bundleCreator
        userName = `${firstName} ${lastName}`
    }

    switch (whatToDelete) {
        case BundleType.journey:
            if (bundleId === null) return {
                option: {
                    label: "TOOLTIP.JOURNEY.CLEAR_JOURNEY",
                    value: 0,
                    raw: {option: DELETE_ACTIVITIES_DROPDOWN_SELECTION.LOCAL_ACTIVITIES}
                }, modalHeadline: "", modalText: ""
            }
            return {
                option: {
                    label: "TOOLTIP.JOURNEY.DELETE_REMOTE_DATA",
                    value: 0,
                    raw: {option: DELETE_ACTIVITIES_DROPDOWN_SELECTION.REMOTE_BUNDLE}
                },
                modalHeadline: "MODALS.DELETE_BUNDLE.JOURNEY_TITLE",
                modalText: t("MODALS.DELETE_BUNDLE.JOURNEY_TEXT", {
                    name: bundleName,
                    userName,
                    interpolation: {escapeValue: false}
                })
            }
        case BundleType.travelComponents:
            if (bundleId === null) return {
                option: {
                    label: "TOOLTIP.TRAVEL_COMPONENTS.CLEAR_TABLE",
                    value: 0,
                    raw: {option: DELETE_ACTIVITIES_DROPDOWN_SELECTION.LOCAL_ACTIVITIES}
                },
                modalHeadline: "",
                modalText: ""
            }
            return {
                option: {
                    label: "TOOLTIP.TRAVEL_COMPONENTS.DELETE_REMOTE_DATA",
                    value: 0,
                    raw: {option: DELETE_ACTIVITIES_DROPDOWN_SELECTION.REMOTE_BUNDLE}
                },
                modalHeadline: "MODALS.DELETE_BUNDLE.TRAVEL_COMPONENTS_TITLE",
                modalText: t("MODALS.DELETE_BUNDLE.TRAVEL_COMPONENTS_TEXT", {
                    name: bundleName,
                    userName,
                    interpolation: {escapeValue: false}
                })
            }
        default:
            whatToDelete satisfies never
    }
    return {option: {} as DropdownOption, modalHeadline: "", modalText: ""}
}

const DeleteBundlesButton = ({
                                 whatToDelete,
                                 disabled,
                                 activities,
                                 onDelete,
                             }: DeleteBundlesButtonProps) => {
    const [modalVisible, setModalVisible] = useState(false)
    const [bundle, setBundle] = useState<JourneyState | TravelComponentsState | null>(null)
    const travelComponents = useAppSelector(state => state.travelComponents)
    const journey = useAppSelector(state => state.journey)
    const {
        bundleId: travelComponentsBundleId,
        activities: travelComponentsActivities,
        name: travelComponentsName,
        description: travelComponentsDescription,
        customer: travelComponentsCustomer,
    } = travelComponents
    const {
        bundleId: journeyBundleId,
        activities: journeyActivities,
        name: journeyName,
        description: journeyDescription,
        customer: journeyCustomer,
        journeyDuration: journeyDurationFromStore,
        personCount: journeyPersonCount
    } = journey
    const {agencies, allUsers} = useAppSelector(state => state.user)
    const {i18n, t} = useTranslation()
    const bundleId = bundle?.bundleId ?? null
    const emptyTravelComponents = isEqual({
        activities: travelComponentsActivities,
        name: travelComponentsName,
        description: travelComponentsDescription,
        customer: travelComponentsCustomer,
        bundleId: travelComponentsBundleId,
    }, DEFAULT_TRAVEL_COMPONENTS)
    const emptyJourney = isEqual({
        bundleId: journeyBundleId,
        name: journeyName,
        description: journeyDescription,
        activities: journeyActivities,
        customer: journeyCustomer,
        journeyDuration: journeyDurationFromStore,
        personCount: journeyPersonCount,
    }, DEFAULT_JOURNEY)
    const [deleteBundle, {
        data: deleteBundleResponse,
        isLoading: isDeletingBundle,
        error: deleteBundleError,
    }] = useDeleteBundleMutation()
    const dispatch = useAppDispatch()
    const userThatCreateBundle = allUsers?.find(user => user.id === bundle?.userId) ?? null
    const {option, modalHeadline, modalText} = generateText(
        whatToDelete,
        bundleId,
        bundle?.name ?? null,
        userThatCreateBundle,
        i18n
    )
    const {label} = option
    const hasActivitiesSelected = Boolean(activities && activities.length > 0)

    useEffect(() => {
        switch (whatToDelete) {
            case BundleType.journey:
                setBundle(journey)
                break;
            case BundleType.travelComponents:
                setBundle(travelComponents)
                break;
            default:
                whatToDelete satisfies never
        }
    }, [travelComponents, journey]);

    useEffect(() => {
        if (deleteBundleResponse?.status === GENERAL_BACKEND_RESPONSE.success) _deleteBundle(DELETE_ACTIVITIES_DROPDOWN_SELECTION.REMOTE_BUNDLE)
    }, [deleteBundleResponse])

    useEffect(() => {
        if (deleteBundleError) _renderToast("TOASTS.ERROR.DELETE_REMOTE_DATA", "error")
    }, [deleteBundleError]);

    const _renderToast = (message: string, type: TypeOptions) => toast(<ToastWithTextOnly
            message={t(message)}/>,
        {progress: undefined, type}
    )

    const _deleteBundle = (option: DELETE_ACTIVITIES_DROPDOWN_SELECTION) => {
        if (hasActivitiesSelected && option === DELETE_ACTIVITIES_DROPDOWN_SELECTION.SELECTED_ACTIVITIES) {
            dispatch(deleteTravelComponents(activities))
            _renderToast("TOASTS.SUCCESS.DELETE_SELECTED_ACTIVITIES", "success")
        } else {
            switch (whatToDelete) {
                case BundleType.journey:
                    dispatch(deleteJourney())
                    break;
                case BundleType.travelComponents:
                    dispatch(deleteTravelComponents())
                    break;
                default:
                    whatToDelete satisfies  never
            }
            if (option === DELETE_ACTIVITIES_DROPDOWN_SELECTION.LOCAL_ACTIVITIES) _renderToast("TOASTS.SUCCESS.DELETE_LOCAL_DATA", "success")
            setModalVisible(false)
        }
        onDelete && onDelete()
    }

    const _onModalConfirmDeleteBundle = () => deleteBundle(bundleId!!)

    const _setDisableButton = () => {
        if (disabled) return true
        switch (whatToDelete) {
            case BundleType.journey:
                return emptyJourney
            case BundleType.travelComponents:
                return emptyTravelComponents
            default:
                whatToDelete satisfies never
        }
    }

    return <>
        {(bundleId !== null || hasActivitiesSelected) ? <DropdownInput
                readOnly={_setDisableButton()}
                useSelection={false}
                iconOnlyListBoxWidth={400}
                label={label}
                options={convertDeleteBundlesButtonOptionsToDropdownFormat(option, whatToDelete, Boolean(bundleId), hasActivitiesSelected, i18n)}
                iconOnlyColor={"primary"}
                iconOnlyIcon={"trash"}
                iconOnlySize={"large"}
                setSelection={selection => {
                    if (selection) {
                        const {raw: {option}} = selection[0]
                        if (option === DELETE_ACTIVITIES_DROPDOWN_SELECTION.SELECTED_ACTIVITIES || option === DELETE_ACTIVITIES_DROPDOWN_SELECTION.LOCAL_ACTIVITIES) {
                            _deleteBundle(option)
                        } else {
                            if (option === DELETE_ACTIVITIES_DROPDOWN_SELECTION.REMOTE_BUNDLE) setModalVisible(true)
                        }
                    }
                }}
                iconOnly={true}/> :
            <IconButton
                disabled={_setDisableButton()}
                tooltip={label}
                color={"primary"}
                size={"large"}
                onlyIcon={true}
                icon={"trash"}
                onClick={() => _deleteBundle(DELETE_ACTIVITIES_DROPDOWN_SELECTION.LOCAL_ACTIVITIES)}/>
        }
        {agencies && <Modal
            closeButtonText={"GENERAL.ABORT"}
            onClose={() => setModalVisible(false)}
            onBackdropClick={() => setModalVisible(false)}
            headline={modalHeadline}
            showPaper={true}
            onEnterKeyPressed={() => _onModalConfirmDeleteBundle()}
            onConfirm={() => _onModalConfirmDeleteBundle()}
            confirmButtonText={"GENERAL.DELETE"}
            confirmButtonColor={"warningMain"}
            showSpinner={isDeletingBundle}
            show={modalVisible}>
            <Text>{modalText}</Text>
        </Modal>}</>
}

export default DeleteBundlesButton